// Must stay in step with the `w-*` utility on each matching `<th>`.
export const COLUMN_WIDTH = {
  index: 64, // w-16
  firm: 192, // w-48
  caseName: 224, // w-56
  status: 144, // w-36
  physician: 176, // w-44
  analyst: 176, // w-44
  qcReviewer: 144, // w-36
  examDate: 112, // w-28
  dueDate: 112, // w-28
  files: 64, // w-16
  pages: 64, // w-16
  createdAt: 112, // w-28
} as const;

// Reserves real layout space so the bar is always visible — macOS's overlay
// scrollbar only surfaces mid-gesture, hiding that columns exist off-screen.
export const TABLE_SCROLLBAR_CLASSES =
  "[&::-webkit-scrollbar]:h-1.5 [&::-webkit-scrollbar]:w-1.5 " +
  "[&::-webkit-scrollbar-track]:bg-transparent " +
  "[&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-thumb]:bg-border " +
  "hover:[&::-webkit-scrollbar-thumb]:bg-border-strong";

// Not a `box-shadow`: browsers drop those on table cells under `border-collapse: collapse`,
// and Tailwind emits no rule at all for `shadow-[...hsl(var(--x)/alpha)]`.
const PINNED_EDGE_CLASSES =
  "after:pointer-events-none after:absolute after:inset-y-0 after:right-0 after:w-3 " +
  "after:translate-x-full after:transition-opacity after:duration-200 " +
  "after:bg-[linear-gradient(to_right,hsl(var(--shadow-color)/0.16),transparent)]";

export const pinnedEdgeClasses = (isVisible: boolean): string =>
  `${PINNED_EDGE_CLASSES} ${isVisible ? "after:opacity-100" : "after:opacity-0"}`;

// Keyed by visible action count, one entry per layout concern so they can't fall out of
// step with each other. Fixed tracks rather than `grid-cols-N` so the icons don't drift
// apart in roles that see fewer. Keep every gridClasses value a whole literal string —
// Tailwind's extractor cannot see a class name composed at runtime.
export const ACTIONS_LAYOUT: Record<
  number,
  { gridClasses: string; headerWidthClass: string; columnWidth: number }
> = {
  4: { gridClasses: "grid-cols-[repeat(4,1.625rem)]", headerWidthClass: "w-32", columnWidth: 128 },
  5: { gridClasses: "grid-cols-[repeat(5,1.625rem)]", headerWidthClass: "w-40", columnWidth: 160 },
  6: { gridClasses: "grid-cols-[repeat(6,1.625rem)]", headerWidthClass: "w-48", columnWidth: 192 },
};

export const ACTION_SLOT_CLASSES = "flex h-[1.625rem] w-[1.625rem] items-center justify-center";
export const ACTION_BUTTON_CLASSES = `group relative rounded-lg transition-colors ${ACTION_SLOT_CLASSES}`;
export const ACTION_ICON_STROKE_WIDTH = 1.5;
export const ACTION_IDLE_CLASSES =
  "text-muted-foreground hover:bg-primary-subtle hover:text-primary-emphasis";
export const ACTION_DESTRUCTIVE_CLASSES =
  "text-muted-foreground hover:bg-destructive-subtle hover:text-destructive-text";
export const ACTION_DISABLED_CLASSES = "cursor-not-allowed text-text-disabled";
export const ACTION_TOOLTIP_CLASSES =
  "invisible absolute bottom-full left-1/2 z-tooltip mb-2 -translate-x-1/2 transform rounded bg-foreground px-2 py-1 text-xs text-white group-hover:visible";

// Cancels the global `:where(a)` label weight and underline, in both the base and hover
// states — color is untouched, so callers must still override it themselves.
export const ANCHOR_RESET_CLASSES = "font-normal no-underline";
// Fills its cell, so the cell's padding moves onto the link and stays clickable.
export const CASE_ROW_LINK_CLASSES = `block text-inherit ${ANCHOR_RESET_CLASSES}`;

export const filterTypes = {
  status: { label: "Status", field: "status_id" },
  physician: { label: "Physician", field: "physician" },
  analyst: { label: "Analyst", field: "analyst" },
} as const;

export type FilterTypeKey = keyof typeof filterTypes;

// Allow-list, not a deny-list — a new filter type is hidden from Professionals by
// default until added here, so a forgotten entry can't leak firm staff names to
// physicians (the Physician and Analyst options come from the case payload).
export const FILTERS_VISIBLE_TO_PROFESSIONAL: FilterTypeKey[] = ["status"];
